import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Trash, Plus, Save } from "lucide-react";

/**
 * Reusable admin CRUD screen for simple entities.
 * `fields`: [{ key, label, type: 'text'|'number'|'textarea'|'color'|'boolean'|'url', span? }]
 */
export default function AdminSimpleList({ title, endpoint, fields, testidPrefix }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const emptyForm = () =>
    fields.reduce((acc, f) => {
      if (f.type === "boolean") acc[f.key] = true;
      else if (f.type === "number") acc[f.key] = 0;
      else if (f.type === "select") acc[f.key] = f.default ?? (f.options?.[0]?.value ?? "");
      else acc[f.key] = "";
      return acc;
    }, { active: true, order: 0 });

  const load = useCallback(async () => {
    const { data } = await api.get(`/${endpoint}`);
    setItems(data);
  }, [endpoint]);
  useEffect(() => { load(); }, [load]);

  const startNew = () => setEditing({ __new: true, ...emptyForm() });
  const cancel = () => setEditing(null);

  const save = async () => {
    const body = { ...editing };
    delete body.__new;
    delete body.id;
    delete body.created_at;
    delete body.updated_at;
    try {
      if (editing.__new) {
        await api.post(`/${endpoint}`, body);
        toast.success("Eklendi");
      } else {
        await api.put(`/${endpoint}/${editing.id}`, body);
        toast.success("Güncellendi");
      }
      setEditing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Kayıt hatası"); }
  };

  const del = async (id) => {
    if (!window.confirm("Silinsin mi?")) return;
    await api.delete(`/${endpoint}/${id}`);
    toast.success("Silindi");
    load();
  };

  return (
    <div data-testid={`admin-${testidPrefix}`}>
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-display text-3xl md:text-4xl font-black text-ink-900">{title}</h1>
        <Button onClick={startNew} className="gradient-primary text-white rounded-full font-bold uppercase text-xs tracking-wider" data-testid={`${testidPrefix}-add`}>
          <Plus className="h-4 w-4 mr-1" /> Yeni Ekle
        </Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-3xl bg-white border border-ink-100 overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-ink-50">
              <tr>
                {fields.slice(0, 3).map((f) => <th key={f.key} className="text-left px-4 py-3 font-semibold">{f.label}</th>)}
                <th className="text-left px-4 py-3 font-semibold">Aktif</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className={`border-t border-ink-100 cursor-pointer hover:bg-brand-50/30 ${editing?.id === it.id ? "bg-brand-50/60" : ""}`} onClick={() => setEditing({ ...it })}>
                  {fields.slice(0, 3).map((f) => (
                    <td key={f.key} className="px-4 py-3">
                      {f.type === "color" && it[f.key] ? (
                        <div className="flex items-center gap-2">
                          <span className="inline-block h-4 w-4 rounded border border-ink-100" style={{ background: it[f.key] }} />
                          <span>{it[f.key]}</span>
                        </div>
                      ) : String(it[f.key] ?? "-")}
                    </td>
                  ))}
                  <td className="px-4 py-3">{it.active ? "✅" : "-"}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={(e) => { e.stopPropagation(); del(it.id); }} className="text-red-500 hover:text-red-700"><Trash className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={fields.length + 2} className="px-4 py-8 text-center text-ink-500">Kayıt yok.</td></tr>}
            </tbody>
          </table>
        </div>

        <div className="rounded-3xl bg-white border border-ink-100 p-6">
          {editing ? (
            <div className="space-y-4" data-testid={`${testidPrefix}-editor`}>
              <h3 className="font-display font-bold text-xl">{editing.__new ? "Yeni Kayıt" : "Düzenle"}</h3>
              {fields.map((f) => (
                <div key={f.key}>
                  <Label className="text-xs uppercase tracking-widest font-bold">{f.label}</Label>
                  {f.type === "textarea" ? (
                    <Textarea rows={3} value={editing[f.key] || ""} onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })} className="mt-1.5" />
                  ) : f.type === "boolean" ? (
                    <div className="mt-1.5 flex items-center gap-3">
                      <Switch checked={!!editing[f.key]} onCheckedChange={(v) => setEditing({ ...editing, [f.key]: v })} />
                      <span className="text-sm text-ink-500">{editing[f.key] ? "Evet" : "Hayır"}</span>
                    </div>
                  ) : f.type === "color" ? (
                    <div className="flex items-center gap-2 mt-1.5">
                      <input type="color" value={editing[f.key] || "#000000"} onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })} className="h-10 w-14 rounded border border-ink-100" />
                      <Input value={editing[f.key] || ""} onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })} />
                    </div>
                  ) : f.type === "select" ? (
                    <Select value={editing[f.key] || f.default || (f.options?.[0]?.value ?? "")} onValueChange={(v) => setEditing({ ...editing, [f.key]: v })}>
                      <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {(f.options || []).map((o) => (
                          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input type={f.type === "number" ? "number" : "text"} step="any" value={editing[f.key] ?? ""} onChange={(e) => setEditing({ ...editing, [f.key]: f.type === "number" ? Number(e.target.value) : e.target.value })} className="mt-1.5" />
                  )}
                </div>
              ))}
              <div>
                <Label className="text-xs uppercase tracking-widest font-bold">Sıra</Label>
                <Input type="number" value={editing.order ?? 0} onChange={(e) => setEditing({ ...editing, order: Number(e.target.value) })} className="mt-1.5" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest font-bold">Aktif</Label>
                <div className="mt-1.5"><Switch checked={!!editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} /></div>
              </div>
              <div className="flex gap-2 pt-2">
                <Button onClick={save} className="gradient-primary text-white rounded-full" data-testid={`${testidPrefix}-save`}><Save className="h-4 w-4 mr-1.5" /> Kaydet</Button>
                <Button variant="outline" onClick={cancel} className="rounded-full">İptal</Button>
              </div>
            </div>
          ) : (
            <div className="text-ink-500 text-sm">Sağdan yeni ekleyebilir ya da soldan bir kayıt seçerek düzenleyebilirsiniz.</div>
          )}
        </div>
      </div>
    </div>
  );
}
